// @ts-nocheck
/**
 * Recipe sheet, one for the whole app (styles: assets/vhp-recipe-sheet.css): the feed, Likes, Search and the planner.
 *  - Content: sections/recipe-sheet.liquid (snippets/vhp-recipe-body.liquid, the same markup as the recipe article)
 *    through the Section Rendering API: the feed from the article URL, the planner from the product URL
 *    (?section_id=recipe-sheet), so the article page itself (and its Recipe schema) stays the only indexable source.
 *    The planner also passes its own body (own recipes, same markup).
 *  - Body behaviour (ticks, servings stepper, video, the ingredient fold) is window.vhpRecipeBody, shared with the article
 *    (assets/vhp-main-recipe.js).
 *  - Half height by default, drag up for full height, drag down to close. A modal <dialog>, so it also opens over
 *    the planner's sheets and reel.
 *  - Opening pushes a history entry (the feed: the article URL; back closes, refresh = real page).
 *  - Prefetches the current + next two cards while swiping.
 *  - Around the content: title, Kochmodus (assets/vhp-cook.js; ticks go both ways), "Zum Meal Plan hinzufügen",
 *    tickable ingredients (long lists folded, addFold), the recipe video, Zutaten then Zubereitung.
 *
 *   window.vhpRecipeSheet.open(href)                  // feed: article href
 *   window.vhpRecipeSheet.open(href | null, {
 *     url,          // where the body comes from (default: href ?section_id=recipe-sheet)
 *     node,         // or a ready body (no fetch)
 *     title,        // shown until the body has its own
 *     snap,         // 'half' (default) | 'full'
 *     media,        // photo / video at the top; default off when opened from a card (its video plays behind)
 *     head,         // node under the title (planner: portions)
 *     host,         // element the dialog lives in while open (planner: its root, for its own click handlers)
 *     keepUrl,      // history entry without the article URL
 *     plan, cook,   // false hides "Zum Meal Plan" / Kochmodus
 *     source,       // analytics
 *     onBody(node), onCook(), onClose(),
 *   })
 *   window.vhpRecipeSheet.setBody(node)  // replace the content of the open sheet
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpRecipeSheet) return;

  const HALF_RATIO = 0.54;
  // recipe_open values the planner API accepts (vhp-planner src/lib/funnelEvents.ts RECIPE_FROM).
  const OPEN_FROM = new Set(['home', 'explore', 'collection', 'likes', 'recipe']);
  const prefetched = new Map();

  /* ---------------------------------------------------------------- fetching */

  const articleUrl = (href) => new URL(href, window.location.origin);

  // Section Rendering API: the recipe-sheet section in the article's context (sections/recipe-sheet.liquid).
  const sheetRequestUrl = (href) => {
    const url = articleUrl(href);
    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', 'recipe-sheet');
    return url.toString();
  };

  const fetchBody = (url) => {
    const key = new URL(url, window.location.origin).toString();
    if (!prefetched.has(key)) {
      const request = fetch(key, { headers: { Accept: 'text/html' } })
        .then((response) => {
          if (!response.ok) throw new Error(`sheet ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-vhp-recipe-body]');
          if (!node) throw new Error('not a recipe');
          return node;
        })
        .catch((error) => {
          prefetched.delete(key);
          throw error;
        });
      prefetched.set(key, request);
    }
    return prefetched.get(key).then((node) => document.importNode(node, true));
  };

  const fetchSheet = (href) => fetchBody(sheetRequestUrl(href));

  const canPrefetch = () => {
    try {
      const conn = navigator.connection || {};
      if (conn.saveData) return false;
      if (/^(slow-2g|2g)$/.test(conn.effectiveType || '')) return false;
    } catch (_) {}
    return true;
  };

  const idle = typeof requestIdleCallback === 'function' ? requestIdleCallback : (fn) => setTimeout(fn, 150);

  // The recipe this page is (a recipe article opened in the swipe feed, sections/main-recipe.liquid below the deck).
  const isPageRecipe = (href) => {
    if (!href || !document.querySelector('[data-vhp-entry-target]')) return false;
    try {
      return new URL(href, location.href).pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '');
    } catch (_) {
      return false;
    }
  };

  document.addEventListener('vhp:deck-card', (event) => {
    if (!canPrefetch()) return;
    const hrefs = (event.detail?.hrefs || []).filter((href) => href && href !== '#' && !isPageRecipe(href));
    idle(() => {
      hrefs.forEach((href) => {
        fetchSheet(href).catch(() => {});
      });
    });
  });

  /* ---------------------------------------------------------------- sheet frame */

  const el = (tag, className, html) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (html) node.innerHTML = html;
    return node;
  };

  const CLOSE_ICON =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M6 18L18 6"/></svg>';

  const state = { open: false, snap: 'closed', href: '', title: '', opts: {}, req: 0 };
  const cookOpen = () => Boolean(window.vhpCook?.isOpen());
  let root = null;
  let sheet = null;
  let scroller = null;
  let body = null;
  let fullH = 0;
  let savedTitle = '';
  let drag = null;
  let hideTimer = 0;

  const topOffset = () => {
    const bar = document.querySelector('.app-topbar');
    if (!bar) return 8;
    // Scrolled out of view (a collection page scrolled down to its recipes): where the bar sits when in view.
    const rect = bar.getBoundingClientRect();
    return Math.max(rect.bottom, rect.height) + 8;
  };

  const geometry = () => {
    fullH = Math.max(320, window.innerHeight - topOffset());
    const half = Math.min(fullH, Math.round(window.innerHeight * HALF_RATIO));
    return { full: 0, half: fullH - half, closed: fullH };
  };

  const applySnap = (snap, animate = true) => {
    if (!sheet) return;
    const g = geometry();
    state.snap = snap;
    sheet.style.height = `${fullH}px`;
    sheet.style.transition = animate ? '' : 'none';
    sheet.style.transform = `translateY(${g[snap]}px)`;
    sheet.dataset.snap = snap;
    root.classList.toggle('is-dim', snap !== 'closed');
  };

  const build = () => {
    if (root) return;
    root = el(
      'dialog',
      'rsheet-root',
      `<div class="rsheet-backdrop" data-rs-close></div>
       <section class="rsheet">
         <div class="rsheet__grab" data-rs-grab><span class="rsheet__handle"></span></div>
         <button type="button" class="rsheet__close" data-rs-close aria-label="${tr('common.close')}">${CLOSE_ICON}</button>
         <div class="rsheet__body" data-rs-scroll>
           <header class="rsheet__head">
             <p class="rsheet__eyebrow" data-rs-eyebrow hidden></p>
             <h2 class="rsheet__title" id="rsheet-title" data-rs-title></h2>
             <p class="rsheet__meta" data-rs-meta hidden></p>
             <div class="rsheet__slot" data-rs-slot></div>
           </header>
           <div data-rs-body></div>
         </div>
       </section>`
    );
    root.setAttribute('aria-labelledby', 'rsheet-title');
    document.body.appendChild(root);
    sheet = root.querySelector('.rsheet');
    scroller = root.querySelector('[data-rs-scroll]');
    body = root.querySelector('[data-rs-body]');
    bindDrag();
    bindSheet();
    window.addEventListener('resize', () => {
      if (state.open) applySnap(state.snap, false);
    });
  };

  /* ---------------------------------------------------------------- drag */

  const bindDrag = () => {
    sheet.addEventListener('pointerdown', (event) => {
      if (event.button || cookOpen()) return;
      const onGrab = event.target.closest('[data-rs-grab], .rsheet__close');
      const interactive = event.target.closest('a, button, input, select, textarea, [data-vhp-recipe-ing]');
      if (event.target.closest('.rsheet__close')) return;
      // In full height the body scrolls; only the handle area drags. In half height anything drags.
      if (state.snap === 'full' && !event.target.closest('[data-rs-grab]')) return;
      if (state.snap === 'half' && interactive && !onGrab) return;
      drag = { id: event.pointerId, y: event.clientY, t: event.timeStamp, base: geometry()[state.snap], active: false, last: event.clientY, lastT: event.timeStamp, v: 0 };
    });

    sheet.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) < 6) return;
        drag.active = true;
        sheet.setPointerCapture(event.pointerId);
        sheet.style.transition = 'none';
      }
      const dt = event.timeStamp - drag.lastT;
      if (dt > 0) drag.v = (event.clientY - drag.last) / dt;
      drag.last = event.clientY;
      drag.lastT = event.timeStamp;
      const g = geometry();
      const ty = Math.max(g.full, Math.min(g.closed, drag.base + dy));
      sheet.style.transform = `translateY(${ty}px)`;
      drag.ty = ty;
    });

    const end = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const wasActive = drag.active;
      const { ty, v } = drag;
      drag = null;
      if (!wasActive) return;
      const g = geometry();
      let target;
      if (v > 0.7) target = state.snap === 'full' ? 'half' : 'closed';
      else if (v < -0.7) target = 'full';
      else {
        target = ['full', 'half', 'closed'].reduce((best, key) => (Math.abs(g[key] - ty) < Math.abs(g[best] - ty) ? key : best), 'half');
      }
      if (target === 'closed') requestClose();
      else applySnap(target);
    };
    sheet.addEventListener('pointerup', end);
    sheet.addEventListener('pointercancel', end);

    // Mouse wheel / trackpad (desktop, 2026-10-17): at half height scrolling down did nothing, the sheet only opened
    // by dragging. A scroll down now opens it fully, where the body scrolls as usual.
    sheet.addEventListener(
      'wheel',
      (event) => {
        if (state.snap !== 'half' || cookOpen() || event.deltaY <= 0) return;
        event.preventDefault();
        applySnap('full');
      },
      { passive: false }
    );
  };

  /* ---------------------------------------------------------------- content */

  const skeleton = () =>
    el(
      'div',
      'vhp-recipe-skel',
      '<div class="vhp-recipe-skel__img"></div><div class="vhp-recipe-skel__line vhp-recipe-skel__line--title"></div><div class="vhp-recipe-skel__line vhp-recipe-skel__line--row"></div><div class="vhp-recipe-skel__line vhp-recipe-skel__line--row"></div>'
    );

  const paintHead = (node) => {
    const d = node?.dataset || {};
    const title = d.title || state.opts.title || tr('common.recipe');
    state.title = title;
    root.querySelector('[data-rs-title]').textContent = title;
    const eyebrow = root.querySelector('[data-rs-eyebrow]');
    eyebrow.textContent = d.category || '';
    eyebrow.hidden = !d.category;
    const meta = root.querySelector('[data-rs-meta]');
    meta.textContent = d.time ? tr('common.min', { n: d.time }) : '';
    meta.hidden = !d.time;
  };

  // "Zum Meal Plan hinzufügen" + Kochmodus under the photo and macros, so they show at half height too. Rendered bodies
  // bring the row (snippets/vhp-recipe-body.liquid): drop what the caller turned off. Built bodies get the same row.
  const addActions = (node) => {
    const hasSteps = Boolean(node.querySelector('.vhp-recipe-steps__item'));
    const showCook = state.opts.cook !== false && hasSteps;
    const showPlan = state.opts.plan !== false && Boolean(node.dataset.productId);
    const rendered = node.querySelector('.rsheet__actions');
    if (rendered) {
      if (!showPlan) {
        // The planner row in the head (snippets/vhp-recipe-head.liquid), the article's two buttons and their line.
        node.querySelectorAll('[data-vrb-door], [data-vhp-strip-choice], [data-vhp-strip-line]').forEach((el) => el.remove());
        rendered.querySelector('[data-rs-plan]')?.remove();
        rendered.querySelector('[data-vhp-plan-cta]')?.remove();
        rendered.querySelectorAll('[data-vhp-plan-cta-note]').forEach((note) => note.remove());
        node.querySelector('[data-vhp-plan-card]')?.remove();
        node.querySelector('.vhp-ings-plan')?.remove(); // the shopping-list line under the ingredients
      }
      if (!showCook) node.querySelectorAll('[data-rs-cook]').forEach((btn) => btn.remove()); // also the one in the Zubereitung head
      if (!rendered.children.length) rendered.remove();
      return;
    }
    if (!showCook && !showPlan) return;
    const row = el('div', 'rsheet__actions');
    if (showPlan) {
      const plan = el('button', 'rs-btn rs-btn--primary rs-btn--plan', tr('rsheet.add_to_plan'));
      plan.dataset.rsPlan = '';
      ['productId', 'handle', 'title', 'slot', 'plannable', 'image', 'kcal', 'protein', 'carbs', 'fat', 'time'].forEach((key) => {
        if (node.dataset[key] != null) plan.dataset[key] = node.dataset[key];
      });
      row.append(plan);
    }
    if (showCook) {
      const cook = el('button', 'rs-btn rs-btn--cook', tr('cook.mode'));
      cook.dataset.rsCook = '';
      row.append(cook);
    }
    row.querySelectorAll('button').forEach((btn) => (btn.type = 'button'));
    const hero = node.querySelector('.vhp-recipe-sheet__hero');
    if (hero) hero.after(row);
    else node.prepend(row);
  };

  // Zutaten then Zubereitung, one after the other (Maxim 2026-11-03: no tabs, no planner line above them). A long
  // ingredient list shows its first FOLD_SHOW rows, the last one fading out, and a link "Alle 14 Zutaten anzeigen"
  // under it (window.vhpRecipeBody.enhance). The rows stay in the markup; print shows all (assets/vhp-main-recipe.css).
  // About, tips and tools are collapsible rows in the markup itself.
  const FOLD_SHOW = 6;
  let foldSeq = 0;
  const FOLD_MIN = 9; // fold only when it hides 3 or more
  const FOLD_CHEV = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';

  const setFold = (list, open) => {
    const btn = list.parentElement?.querySelector(':scope > .rs-fold');
    if (!btn) return;
    list.classList.toggle('is-folded', !open);
    btn.setAttribute('aria-expanded', String(open));
    btn.querySelector('.rs-fold__label').textContent = open
      ? tr('rsheet.ings_less')
      : tr('rsheet.ings_all', { n: list.querySelectorAll('.vhp-recipe-ings__item').length });
  };

  const addFold = (node) => {
    const list = node.querySelector('.vhp-recipe-ings');
    if (!list || list.dataset.rsFold) return;
    const items = list.querySelectorAll(':scope > .vhp-recipe-ings__item');
    if (items.length < FOLD_MIN) return;
    list.dataset.rsFold = '1';
    // everything after the FOLD_SHOW-th ingredient folds, group heads included
    let seen = 0;
    [...list.children].forEach((li) => {
      if (seen >= FOLD_SHOW) li.classList.add('rs-fold-more');
      if (li.classList.contains('vhp-recipe-ings__item')) seen += 1;
    });
    if (!list.id) list.id = `rs-ings-${(foldSeq += 1)}`;
    const btn = el('button', 'rs-fold', `<span class="rs-fold__label"></span>${FOLD_CHEV}`);
    btn.type = 'button';
    btn.dataset.rsFold = '';
    btn.setAttribute('aria-controls', list.id);
    list.after(btn);
    // "Liste für dieses Rezept" belongs to the whole list: it moves in after the last row, so the fold hides it too
    // (Maxim 2026-11-03); the actions row under the fold keeps the mail pill.
    const copy = node.querySelector('[data-vhp-ings-copy]');
    if (copy) btn.before(copy);
    setFold(list, false);
  };

  // The part an anchor names: #id on the article, data-vrb-anchor in both (the sheet has no ids).
  const anchorOf = (node, id) => {
    try {
      return (id && (node.querySelector(`#${CSS.escape(id)}`) || node.querySelector(`[data-vrb-anchor="${CSS.escape(id)}"]`))) || null;
    } catch (_) {
      return null;
    }
  };

  // The folded ingredient or collapsible row holding #id (an anchor on the article), opened.
  const reveal = (node, id) => {
    const target = anchorOf(node, id);
    if (!target) return false;
    if (target.closest('.rs-fold-more')) setFold(target.closest('.vhp-recipe-ings'), true);
    const row = target.closest('details');
    if (row) row.open = true;
    return true;
  };

  // Which collapsible rows get opened (tools above all: affiliate). Once per body.
  const trackRows = (node) => {
    if (node.dataset.rsRows) return;
    node.dataset.rsRows = '1';
    node.addEventListener(
      'toggle',
      (event) => {
        const row = event.target;
        if (row instanceof HTMLDetailsElement && row.open && row.dataset.section) {
          window.vhpTrack?.('recipe_section_open', { section: row.dataset.section });
        }
      },
      true
    );
  };

  const enhance = (node) => {
    addFold(node);
    trackRows(node);
    window.vhpPortionCalc?.mount(node); // portion calculator (snippets/vhp-portion-calc.liquid, assets/vhp-plan-goal.js)
  };

  // The article's head comes with the body (snippets/vhp-recipe-head.liquid): the frame's title steps back (it still
  // names the dialog) and the caller's head (planner: portions) goes under the meta. Built bodies keep the frame's.
  const mountHead = (node) => {
    const own = node.querySelector('[data-vrb-head]');
    root.classList.toggle('has-head', Boolean(own));
    const slot = root.querySelector('[data-rs-slot]');
    if (own && slot.childNodes.length) {
      own.querySelector('[data-vrb-portions]')?.remove(); // the caller's head says hers (planner)
      const wrap = el('div', 'rsheet__slot');
      wrap.append(...slot.childNodes);
      (own.querySelector('.vrp-meta') || own.lastElementChild)?.after(wrap);
    }
    const likes = window.vhpPlannerLikes;
    node.querySelectorAll('[data-vhp-save]').forEach((btn) => {
      const on = Boolean(likes?.likeItems?.().some((item) => String(item.id) === btn.dataset.vhpSave));
      btn.setAttribute('aria-pressed', String(on));
    });
  };

  const mountBody = (node) => {
    addActions(node);
    enhance(node);
    paintHead(node);
    mountHead(node);
    body.replaceChildren(node);
    window.vhpRating?.mount(node); // "Schon nachgekocht?" + the reviews row (snippets/vhp-reviews.liquid)
    if (state.title) {
      savedTitle = savedTitle || document.title;
      document.title = state.title;
    }
    try {
      state.opts.onBody?.(node);
    } catch (error) {
      console.error('[vhp-recipe-sheet] onBody', error);
    }
  };

  const currentBody = () => body?.querySelector('[data-vhp-recipe-body]') || null;

  const pauseMedia = () => {
    body?.querySelectorAll('video').forEach((video) => video.pause());
    body?.querySelectorAll('[data-vhp-recipe-video].is-on').forEach((wrap) => {
      wrap.classList.remove('is-on');
      wrap.querySelectorAll('video, iframe, .vvc').forEach((media) => media.remove());
    });
  };

  // Our own controls on an uploaded recipe video (Maxim 2026-11-03; YouTube / Vimeo keep their players): tap the video
  // to play / pause, a glass bar (play, scrubber, time, full screen) that hides itself while playing, and a "Ton an"
  // pill while it is muted. Off screen it pauses and picks up again when it comes back.
  const VIDEO_ICON = {
    play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5.5v13a1 1 0 0 0 1.5.9l10.4-6.5a1 1 0 0 0 0-1.8L9.5 4.6A1 1 0 0 0 8 5.5z"/></svg>',
    pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect fill="currentColor" x="6" y="5" width="4.2" height="14" rx="1.3"/><rect fill="currentColor" x="13.8" y="5" width="4.2" height="14" rx="1.3"/></svg>',
    replay: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3"/><path d="M4.5 4.5v4.2h4.2"/></svg>',
    muted: '<svg class="vvc__ico-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M22 9l-6 6M16 9l6 6"/></svg>',
    sound: '<svg class="vvc__ico-sound" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/></svg>',
    full: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
  };

  const clock = (s) => {
    const t = Math.max(0, Math.floor(Number.isFinite(s) ? s : 0));
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
  };

  const videoControls = (wrap, video) => {
    const root = document.createElement('div');
    root.className = 'vvc';
    root.innerHTML = `
      <button type="button" class="vvc__stage" aria-label="${tr('common.video_play')}"></button>
      <span class="vvc__big" aria-hidden="true"><span class="vvc__big-play">${VIDEO_ICON.play}</span><span class="vvc__big-replay">${VIDEO_ICON.replay}</span></span>
      <button type="button" class="vvc__sound" aria-label="${tr('common.sound_on')}">${VIDEO_ICON.muted}${VIDEO_ICON.sound}<span class="vvc__sound-label">${tr('reel.sound_on')}</span></button>
      <div class="vvc__bar">
        <button type="button" class="vvc__btn vvc__pp" aria-label="${tr('common.video_play')}">${VIDEO_ICON.play}${VIDEO_ICON.pause}</button>
        <div class="vvc__track" role="slider" tabindex="0" aria-label="${tr('common.video_seek')}" aria-valuemin="0" aria-valuemax="0" aria-valuenow="0">
          <span class="vvc__rail"><span class="vvc__fill"></span></span><span class="vvc__knob"></span>
        </div>
        <span class="vvc__time">0:00</span>
        <button type="button" class="vvc__btn vvc__fs" aria-label="${tr('common.video_fullscreen')}">${VIDEO_ICON.full}</button>
      </div>`;
    wrap.append(root);
    const $ = (sel) => root.querySelector(sel);
    const stage = $('.vvc__stage');
    const pp = $('.vvc__pp');
    const sound = $('.vvc__sound');
    const track = $('.vvc__track');
    const time = $('.vvc__time');

    let frame = 0;
    let hideTimer = 0;
    let scrub = null;
    let offscreen = false;

    const paint = (forced) => {
      const d = video.duration;
      const known = Number.isFinite(d) && d > 0;
      const at = forced != null && known ? forced * d : video.currentTime;
      root.style.setProperty('--p', String(known ? Math.min(1, at / d) : 0));
      const text = known ? `${clock(at)} / ${clock(d)}` : clock(at);
      if (time.textContent !== text) time.textContent = text;
      track.setAttribute('aria-valuemax', String(Math.round(known ? d : 0)));
      track.setAttribute('aria-valuenow', String(Math.round(at)));
      track.setAttribute('aria-valuetext', text);
    };
    const tick = () => {
      frame = 0;
      if (!video.isConnected) return;
      paint();
      if (!video.paused) frame = requestAnimationFrame(tick);
    };
    const hideLater = () => {
      clearTimeout(hideTimer);
      if (!video.paused && !scrub) hideTimer = setTimeout(() => root.classList.add('is-idle'), 2400);
    };
    const wake = () => {
      root.classList.remove('is-idle');
      hideLater();
    };
    const state = () => {
      const playing = !video.paused;
      root.classList.toggle('is-playing', playing);
      root.classList.toggle('is-ended', video.ended);
      root.classList.toggle('is-muted', video.muted);
      const label = tr(playing ? 'common.video_pause' : video.ended ? 'common.video_replay' : 'common.video_play');
      stage.setAttribute('aria-label', label);
      pp.setAttribute('aria-label', label);
      sound.setAttribute('aria-label', tr(video.muted ? 'common.sound_on' : 'common.sound_off'));
    };
    // The glyph that pops in the middle on a tap (play or pause, whatever just happened).
    const pop = (icon) => {
      const glyph = document.createElement('span');
      glyph.className = 'vvc__pop';
      glyph.setAttribute('aria-hidden', 'true');
      glyph.innerHTML = VIDEO_ICON[icon];
      glyph.addEventListener('animationend', () => glyph.remove());
      root.append(glyph);
    };
    const play = () => void video.play()?.catch?.(() => {});
    const toggle = (withPop) => {
      if (video.paused) {
        play();
        if (withPop) pop('play');
      } else {
        video.pause();
        if (withPop) pop('pause');
      }
      wake();
    };

    stage.addEventListener('click', () => toggle(!video.paused));
    pp.addEventListener('click', () => toggle(false));
    sound.addEventListener('click', () => {
      video.muted = !video.muted;
      if (!video.muted && !video.volume) video.volume = 1;
      if (!video.muted && video.paused) play();
      wake();
    });
    $('.vvc__fs').addEventListener('click', () => {
      if (document.fullscreenElement) return void document.exitFullscreen?.().catch?.(() => {});
      // iPhone Safari can only put the video itself in full screen (with its own controls there).
      if (document.fullscreenEnabled && wrap.requestFullscreen) void wrap.requestFullscreen().catch(() => {});
      else video.webkitEnterFullscreen?.();
    });

    const seekTo = (x) => {
      const r = track.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (x - r.left) / r.width));
      if (Number.isFinite(video.duration)) video.currentTime = p * video.duration;
      paint(p);
    };
    track.addEventListener('pointerdown', (event) => {
      if (event.button) return;
      event.stopPropagation(); // not a sheet drag
      event.preventDefault();
      track.setPointerCapture(event.pointerId);
      scrub = { id: event.pointerId, resume: !video.paused };
      root.classList.add('is-scrub');
      wake();
      video.pause();
      seekTo(event.clientX);
    });
    track.addEventListener('pointermove', (event) => {
      if (scrub?.id === event.pointerId) seekTo(event.clientX);
    });
    const endScrub = (event) => {
      if (scrub?.id !== event.pointerId) return;
      const { resume } = scrub;
      scrub = null;
      root.classList.remove('is-scrub');
      if (resume) play();
      wake();
    };
    track.addEventListener('pointerup', endScrub);
    track.addEventListener('pointercancel', endScrub);
    track.addEventListener('keydown', (event) => {
      const d = video.duration;
      if (!Number.isFinite(d)) return;
      const to = { ArrowLeft: video.currentTime - 5, ArrowRight: video.currentTime + 5, Home: 0, End: d }[event.key];
      if (to == null) return;
      event.preventDefault();
      video.currentTime = Math.min(d, Math.max(0, to));
      paint();
      wake();
    });

    root.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'mouse') wake();
    });
    root.addEventListener('focusin', wake);
    video.addEventListener('play', () => {
      state();
      if (!frame) frame = requestAnimationFrame(tick);
      hideLater();
    });
    video.addEventListener('pause', () => {
      state();
      paint();
      if (!offscreen) wake();
    });
    video.addEventListener('ended', state);
    video.addEventListener('volumechange', state);
    // The buffering ring only after 800 ms of waiting (2026-11-04, docs/RECIPE_COLD_READ.md 8: it sat on the food while
    // the first frames loaded); the poster stays until the first frame plays (wrap.is-live, assets/vhp-recipe-sheet.css).
    let waitTimer = 0;
    video.addEventListener('waiting', () => {
      window.clearTimeout(waitTimer);
      waitTimer = window.setTimeout(() => root.classList.add('is-wait'), 800);
    });
    video.addEventListener('playing', () => {
      window.clearTimeout(waitTimer);
      root.classList.remove('is-wait');
      wrap.classList.add('is-live');
    });
    // timeupdate too: frames do not run in a background tab.
    ['loadedmetadata', 'durationchange', 'seeked', 'timeupdate'].forEach((type) => video.addEventListener(type, () => scrub || paint()));

    if ('IntersectionObserver' in window) {
      const view = new IntersectionObserver(
        ([entry]) => {
          if (!video.isConnected) return void view.disconnect();
          if (entry.intersectionRatio < 0.25 && !video.paused) {
            offscreen = true;
            video.pause();
          } else if (entry.intersectionRatio >= 0.25 && offscreen) {
            offscreen = false;
            play();
          }
        },
        { threshold: [0, 0.25] }
      );
      view.observe(wrap);
    }

    state();
    paint();
  };

  // muted: the video starting by itself (assets/vhp-main-recipe.js); a tap on the poster starts it with sound.
  const startVideo = (wrap, { muted = false } = {}) => {
    if (!wrap || wrap.classList.contains('is-on')) return;
    const media = wrap.querySelector('template')?.content?.firstElementChild?.cloneNode(true);
    if (!media) return;
    wrap.classList.add('is-on');
    const own = media instanceof HTMLVideoElement;
    if (own) {
      media.controls = false;
      media.removeAttribute('controls');
      media.playsInline = true;
      media.setAttribute('playsinline', '');
      media.defaultMuted = muted;
      media.muted = muted;
    }
    wrap.append(media);
    // YouTube / Vimeo never say when they play: the poster goes with the iframe.
    if (!own) wrap.classList.add('is-live');
    if (own) {
      videoControls(wrap, media);
      media.play()?.catch?.(() => {
        media.muted = true;
        void media.play().catch(() => {});
      });
    }
  };

  /* ---------------------------------------------------------------- body behaviour (sheet + article) */

  // Page-language decimals and units, like snippets/vhp-recipe-amount.liquid ("0,5 TL" / "0.5 tsp").
  const formatAmount = (value) => {
    if (!Number.isFinite(value)) return '';
    const roundedInt = Math.round(value);
    if (Math.abs(value - roundedInt) < 1e-6) return String(roundedInt);
    const text = String(Math.round(value * 10) / 10).replace(/\.0$/, '');
    return document.documentElement.lang === 'de' ? text.replace('.', ',') : text;
  };
  const UNIT_KEY = { el: 'el', esslöffel: 'el', tl: 'tl', teelöffel: 'tl', stück: 'stueck', stk: 'stueck', prise: 'prise' };
  const unitLabel = (unit) => {
    const key = UNIT_KEY[unit.toLocaleLowerCase('de').replace(/\./g, '')];
    return key ? tr(`units.${key}`) : unit;
  };

  // Her portion as the calculator says it: ¾, 1½ (quarters, like assets/vhp-plan-goal.js qty).
  const QUARTER = { 0.25: '¼', 0.5: '½', 0.75: '¾' };
  const portionQty = (n) => {
    const whole = Math.floor(n + 1e-9);
    return `${whole || ''}${QUARTER[Math.round((n - whole) * 4) / 4] || ''}` || '0';
  };

  // The stepper's ladder with her portion: quarters up to 1, halves up to 12, plus her own portion (1¼ stays reachable).
  const portionLadder = (mine) => {
    const steps = [0.25, 0.5, 0.75];
    for (let n = 1; n <= 12; n += 0.5) steps.push(n);
    if (!steps.some((s) => Math.abs(s - mine) < 1e-6)) steps.push(mine);
    return steps.sort((a, b) => a - b);
  };

  // Servings stepper: rescales the amounts of its body (data-portions = the recipe's portions). With her portion
  // (docs/PORTION_FIRST.md batch 1: data-yours = on, data-yours-scale = her share of one recipe portion, set by
  // assets/vhp-plan-goal.js) it counts in portions ("¾ Portion", Maxim 11-04: "1 Person" next to "¾ Portion für dich"
  // read as not her portion), stepping freely (¼ up to 1, then ½: 1, 1½, 2 …). It starts at the recipe's own
  // portions, the amount she cooks (2026-11-04, docs/RECIPE_COLD_READ.md 2b); the line under it names her portion.
  // data-yours-total holds the pick until her portion changes. Rounded like the planner's lists (assets/vhp-amounts.js);
  // the line under it follows (vhp:servings → assets/vhp-plan-goal.js paintIngs). delta 0 repaints.
  const stepServings = (node, delta) => {
    const portions = Number(node.dataset.portions) > 0 ? Number(node.dataset.portions) : 1;
    const scale = Number(node.dataset.yoursScale) || 0;
    const yours = node.dataset.yours === 'on' && scale > 0 && Boolean(window.vhpAmounts);
    const unitEl = node.querySelector('[data-serv-unit]');
    if (yours) {
      if (node.dataset.yoursBase !== String(scale)) {
        node.dataset.yoursBase = String(scale);
        node.dataset.yoursTotal = String(portions);
      }
      const ladder = portionLadder(scale);
      let total = Number(node.dataset.yoursTotal) || scale;
      if (delta > 0) total = ladder.find((s) => s > total + 1e-6) ?? total;
      else if (delta < 0) total = [...ladder].reverse().find((s) => s < total - 1e-6) ?? total;
      node.dataset.yoursTotal = String(total);
      const cap = node.querySelector('[data-vhp-ings-yours]');
      const tpl = cap ? (total <= 1 ? cap.dataset.tOne : cap.dataset.tOther) : '';
      node.querySelectorAll('[data-serv-num]').forEach((num) => (num.textContent = portionQty(total)));
      if (unitEl) unitEl.textContent = (tpl || unitEl.dataset.tServ || '').replace('{n}', '').trim();
      if (delta) node.dispatchEvent(new CustomEvent('vhp:servings', { bubbles: true }));
      node.querySelectorAll('[data-serv="-1"]').forEach((btn) => (btn.disabled = total <= ladder[0] + 1e-6));
      node.querySelectorAll('[data-serv="1"]').forEach((btn) => (btn.disabled = total >= ladder[ladder.length - 1] - 1e-6));
      node.querySelectorAll('.vhp-recipe-ings__amt[data-base]').forEach((amt) => {
        const base = parseFloat(amt.dataset.base);
        if (!base) return;
        amt.textContent = window.vhpAmounts.text((base / portions) * total, (amt.dataset.unit || '').trim(), unitLabel, { space: true });
      });
      return;
    }
    const current = Number(node.dataset.servings) || portions;
    const servings = Math.min(12, Math.max(1, current + delta));
    node.dataset.servings = String(servings);
    node.querySelectorAll('[data-serv-num]').forEach((num) => (num.textContent = servings));
    if (unitEl) unitEl.textContent = unitEl.dataset.tServ || unitEl.textContent;
    node.querySelectorAll('[data-serv="-1"]').forEach((btn) => (btn.disabled = servings <= 1));
    node.querySelectorAll('[data-serv="1"]').forEach((btn) => (btn.disabled = servings >= 12));
    node.querySelectorAll('.vhp-recipe-ings__amt[data-base]').forEach((amt) => {
      const base = parseFloat(amt.dataset.base);
      if (!base) return;
      const unit = (amt.dataset.unit || '').trim();
      const value = formatAmount((base / portions) * servings);
      amt.textContent = unit ? `${value} ${unitLabel(unit)}` : value;
    });
  };

  // "Liste für dieses Rezept" (2026-11-01, docs/RECIPE_CRO_ATTACK_PLAN.md P3): the ingredient rows as they stand (her
  // portion or the stepper's count), group heads as lines, to the share sheet where there is one, else the clipboard.
  const listText = (node, btn) => {
    const lines = [];
    node.querySelectorAll('.vhp-recipe-ings > li').forEach((li) => {
      if (li.classList.contains('vhp-recipe-ings__group')) {
        lines.push('', `${li.textContent.trim()}:`);
        return;
      }
      if (!li.hasAttribute('data-vhp-recipe-ing')) return;
      const amt = li.querySelector('.vhp-recipe-ings__amt')?.textContent.replace(/\s+/g, ' ').trim() || '';
      const nameEl = li.querySelector('.vhp-recipe-ings__name');
      const note = nameEl?.querySelector('.vhp-recipe-ings__note')?.textContent.trim() || '';
      const name = (nameEl?.firstChild?.textContent || nameEl?.textContent || '').trim();
      lines.push(`- ${amt ? `${amt} ` : ''}${name}${note ? ` (${note})` : ''}`);
    });
    const head = [btn.dataset.title || '', btn.dataset.url || ''].filter(Boolean);
    return `${head.join('\n')}\n${lines.join('\n').replace(/^\n+/, '')}`.trim();
  };
  const copyList = async (node, btn) => {
    const text = listText(node, btn);
    const done = () => {
      btn.classList.add('is-done');
      node.querySelector('[data-vhp-plan-cta="list"]')?.classList.add('is-after-copy');
      window.vhpTrack?.('recipe_list_copy', {});
    };
    if (navigator.share && window.matchMedia?.('(pointer: coarse)').matches) {
      try {
        await navigator.share({ title: btn.dataset.title || document.title, text });
        done();
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast(btn.dataset.tCopied || '');
      done();
      return;
    } catch (e) {
      /* no clipboard permission (in-app browsers, previews): the old way, then the text itself */
    }
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
    document.body.append(area);
    area.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (e) {
      copied = false;
    }
    area.remove();
    if (copied) {
      toast(btn.dataset.tCopied || '');
      done();
      return;
    }
    let pre = btn.nextElementSibling;
    if (!pre || !pre.classList.contains('vhp-ings-copy__text')) {
      pre = document.createElement('pre');
      pre.className = 'vhp-ings-copy__text';
      btn.after(pre);
    }
    pre.textContent = text;
    pre.hidden = false;
    toast(btn.dataset.tFailed || '');
  };

  // Clicks inside a recipe body; true when handled.
  const bodyClick = (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return false;
    const node = t.closest('[data-vhp-recipe-body]');
    if (!node) return false;
    const serv = t.closest('[data-serv]');
    if (serv) {
      stepServings(node, Number(serv.dataset.serv));
      return true;
    }
    const play = t.closest('[data-vhp-recipe-play]');
    if (play) {
      startVideo(play.closest('[data-vhp-recipe-video]'));
      return true;
    }
    const copy = t.closest('[data-vhp-ings-copy]');
    if (copy) {
      copyList(node, copy);
      return true;
    }
    const fold = t.closest('[data-rs-fold]');
    const list = fold && node.querySelector(`#${fold.getAttribute('aria-controls')}`);
    if (list) {
      const open = list.classList.contains('is-folded');
      // Closing from below: the pill stays where the finger is instead of the method sliding under it.
      const before = fold.getBoundingClientRect().top;
      setFold(list, open);
      if (open) window.vhpTrack?.('recipe_section_open', { section: 'ingredients_all' });
      else (fold.closest('[data-rs-scroll]') || window).scrollBy({ top: fold.getBoundingClientRect().top - before, behavior: 'instant' });
      return true;
    }
    const ing = t.closest('[data-vhp-recipe-ing]');
    if (ing) {
      ing.classList.toggle('is-on');
      return true;
    }
    return false;
  };

  window.vhpRecipeBody = { click: bodyClick, enhance, reveal, repaint: (node) => stepServings(node, 0), startVideo };

  /* ---------------------------------------------------------------- history */

  const layer = () => history.state?.vhpLayer || 0;

  window.addEventListener('popstate', () => {
    if (state.open && layer() < 1) closeSheetUi();
  });

  /* ---------------------------------------------------------------- open / close */

  // A recipe article opened in the swipe feed has the full recipe right below the deck (sections/main-recipe.liquid):
  // its own card scrolls down to it instead of loading the same recipe into the sheet.
  const scrollToPageRecipe = (href) => {
    const target = isPageRecipe(href) && document.querySelector('[data-vhp-entry-target]');
    if (!target) return false;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    return true;
  };

  const open = async (href, opts = {}) => {
    if (state.open || (!href && !opts.url && !opts.node) || href === '#') return;
    if (!opts.url && !opts.node && scrollToPageRecipe(href)) return;
    build();
    window.clearTimeout(hideTimer);
    state.open = true;
    state.href = href || '';
    state.opts = opts;
    const req = (state.req += 1);

    const host = opts.host || document.body;
    if (root.parentElement !== host) {
      // Moving an open dialog would drop it out of the top layer: close it first (it may still be sliding out).
      if (root.open) root.close();
      host.appendChild(root);
    }
    root.querySelector('[data-rs-slot]').replaceChildren(...(opts.head ? [opts.head] : []));
    root.classList.toggle('is-bare', !(opts.media ?? !href));
    paintHead(null);
    body.replaceChildren(skeleton());
    scroller.scrollTop = 0;
    document.documentElement.classList.add('rs-open');
    if (!root.open) root.showModal();
    applySnap('closed', false);
    sheet.offsetHeight; // commit the closed position before animating in
    applySnap(opts.snap === 'full' ? 'full' : 'half');

    try {
      const url = href && !opts.keepUrl ? articleUrl(href).pathname : location.href;
      history.pushState({ ...(history.state || {}), vhpLayer: 1, vhpSheet: href || true }, '', url);
    } catch (_) {}
    if (typeof window.vhpTrack === 'function') window.vhpTrack('recipe_sheet_open', { recipe_url: href || opts.url || '', source: opts.source || 'feed' });
    // Where recipes get opened, as an anonymous funnel step (docs/HOMEPAGE_FUNNEL.md): the page type, or "search" for
    // an Explore search. Only whitelisted values (the API drops a batch with an unknown one).
    const page = window.vhpTrack?.pageType?.();
    const searching = page === 'explore' && Boolean(document.querySelector('[data-s-input]')?.value?.trim());
    window.vhpPlannerEvents?.step('recipe_open', searching ? 'search' : OPEN_FROM.has(page) ? page : 'other');

    if (opts.node) {
      mountBody(opts.node);
      return;
    }
    try {
      const node = await fetchBody(opts.url || sheetRequestUrl(href));
      if (!state.open || req !== state.req) return;
      mountBody(node);
    } catch (error) {
      if (!state.open || req !== state.req) return;
      if (!opts.url && href) {
        // Not a sheet-able recipe: just go to the real page.
        state.open = false;
        window.location.href = href;
        return;
      }
      console.error('[vhp-recipe-sheet] load failed', opts.url, error);
      body.replaceChildren(el('p', 'rsheet__error', tr('rsheet.load_failed')));
    }
  };

  const setBody = (node) => {
    if (state.open && node) mountBody(node);
  };

  const closeSheetUi = () => {
    if (!state.open) return;
    state.open = false;
    state.req += 1;
    const { onClose } = state.opts;
    pauseMedia();
    applySnap('closed');
    document.documentElement.classList.remove('rs-open');
    if (savedTitle) {
      document.title = savedTitle;
      savedTitle = '';
    }
    hideTimer = window.setTimeout(() => {
      if (state.open || !root) return;
      if (root.open) root.close();
      body.replaceChildren();
    }, 320);
    try {
      onClose?.();
    } catch (error) {
      console.error('[vhp-recipe-sheet] onClose', error);
    }
  };

  const requestClose = () => {
    if (!state.open) return;
    if (layer() >= 1) history.back();
    else closeSheetUi();
  };

  /* ---------------------------------------------------------------- cook mode */
  // Shared cook mode (assets/vhp-cook.js), read from the sheet; ticks go both ways. "+ Plan" sits in its top bar.

  const openCook = () => {
    const node = currentBody();
    if (!node || !window.vhpCook) return;
    const { ingredients, steps, done } = window.vhpCook.read(node, 'sheet');
    const planSource = node.querySelector('[data-rs-plan]');
    let planBtn = null;
    if (planSource) {
      planBtn = planSource.cloneNode(true);
      planBtn.className = 'cook__plan';
      planBtn.textContent = tr('rsheet.plan_short');
    }
    try {
      state.opts.onCook?.();
    } catch (_) {}
    window.vhpCook.open({
      title: state.title,
      source: state.opts.source || 'sheet',
      rateId: node.dataset.productId,
      ingredients,
      steps,
      done,
      actions: [planBtn],
      // "Fertig": the planner card under the rating for non-subscribers (assets/vhp-plan-goal.js, docs/RECIPE_FUNNEL.md 3.1).
      plan: window.vhpPlanNext?.cookCard(node) || null,
      onClose: (ticked) => {
        const on = new Set(ticked);
        [...node.querySelectorAll('.vhp-recipe-ings__item')]
          .filter((row) => row.querySelector('.vhp-recipe-ings__name')?.textContent.trim())
          .forEach((row, i) => row.classList.toggle('is-on', on.has(i)));
      },
    });
  };

  // Teilen in the head: the article (the article page shares itself, assets/vhp-main-recipe.js).
  const shareRecipe = async (node) => {
    const url = new URL(node.dataset.articleUrl || state.href || location.href, location.origin).toString();
    const title = node.dataset.title || state.title || document.title;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast(tr('common.link_copied'));
    } catch (_) {
      toast(tr('reels.share_failed'));
    }
  };

  // A part of the body by its anchor, opened and scrolled to the top of the sheet (full height).
  const jumpTo = (node, id) => {
    if (!anchorOf(node, id)) return;
    reveal(node, id);
    if (state.snap !== 'full') applySnap('full');
    const target = anchorOf(node, id);
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollTo({
      top: target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 12,
      behavior: reduce ? 'auto' : 'smooth',
    });
  };

  // Clicks inside the sheet. "Zum Meal Plan" is wired on the document (it is also in cook mode).
  const bindSheet = () => {
    root.addEventListener('click', (event) => {
      const t = event.target;
      if (!(t instanceof Element)) return;
      if (t.closest('[data-rs-close]')) {
        requestClose();
        return;
      }
      if (t.closest('[data-rs-cook]')) {
        openCook();
        return;
      }
      const node = t.closest('[data-vhp-recipe-body]');
      if (node && t.closest('[data-action="share"]')) {
        event.preventDefault();
        shareRecipe(node);
        return;
      }
      // # links of the head (#about, #bewerten): open and scroll inside the sheet, not the page's hash (that would
      // close the sheet through popstate). The choice buttons scroll themselves (assets/vhp-plan-goal.js).
      const hash = node && !t.closest('[data-vhp-choice]') ? t.closest('a[href^="#"]') : null;
      if (hash) {
        event.preventDefault();
        jumpTo(node, decodeURIComponent(hash.getAttribute('href').slice(1)));
        return;
      }
      bodyClick(event);
    });
    // Escape: through history like ✕. Cook mode (its own dialog) closes itself first.
    root.addEventListener('cancel', (event) => {
      event.preventDefault();
      if (!cookOpen()) requestClose();
    });
  };

  /* ---------------------------------------------------------------- meal plan */

  const plannerUrl = () => window.vhpAccount?.plannerUrl || '/pages/meal-planner';
  let toastEl = null;
  let toastTimer = 0;

  const toast = (text, link) => {
    toastEl?.remove();
    toastEl = el('div', 'rs-toast', '');
    toastEl.setAttribute('role', 'status');
    const span = el('span');
    span.textContent = text;
    toastEl.appendChild(span);
    if (link) {
      const a = el('a');
      a.href = link.href;
      a.textContent = link.label;
      a.dataset.track = 'planner_cta_click';
      a.dataset.trackSource = 'plan_added_toast';
      toastEl.appendChild(a);
    }
    document.body.appendChild(toastEl);
    window.requestAnimationFrame(() => toastEl?.classList.add('is-in'));
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toastEl?.remove();
      toastEl = null;
    }, 5000);
  };

  const showPaywall = (source, title, id) => {
    document.querySelector('.rs-paywall')?.remove();
    const node = el(
      'div',
      'rs-paywall',
      `<div class="rs-paywall__backdrop" data-rs-paywall-close></div>
       <section class="rs-paywall__card" role="dialog" aria-modal="true" aria-label="Meal Planner">
         <p class="rs-paywall__eyebrow">Meal Planner</p>
         <h2 class="rs-paywall__title"></h2>
         <p class="rs-paywall__text"></p>
         <a class="rs-paywall__cta" href="${plannerUrl()}" data-track="planner_cta_click" data-track-source="paywall_${source}">${tr('rsheet.paywall_cta')}</a>
         <button type="button" class="rs-paywall__later" data-rs-paywall-close>${tr('common.later')}</button>
       </section>`
    );
    // Lead with what is free (the preview), and how far their likes already are; the subscription comes on the
    // planner's own paywall.
    const likes = window.vhpPlannerLikes;
    const ready = Boolean(likes?.planReady?.());
    const left = likes ? Math.max(0, likes.LIKE_READY - likes.likeCount()) : 0;
    node.querySelector('.rs-paywall__title').textContent = title ? tr('rsheet.saved') : tr('rsheet.paywall_title');
    node.querySelector('.rs-paywall__text').textContent =
      (title ? tr('rsheet.in_likes', { title }) : '') +
      (ready
        ? tr('rsheet.likes_enough')
        : left > 0
          ? tr('rsheet.likes_left', { likes: tr('common.likes', { n: left }) })
          : '') +
      tr('rsheet.paywall_text');
    document.body.appendChild(node);
    window.requestAnimationFrame(() => node.classList.add('is-in'));
    // The planner names this dish on its first screen (paintIntro in assets/vhp-planner-funnel.js).
    node.querySelector('.rs-paywall__cta').addEventListener('click', () => {
      if (!id) return;
      try {
        sessionStorage.setItem('vhp_planner_pick', JSON.stringify({ id: String(id) }));
      } catch (e) {
        /* private mode */
      }
    });
    window.vhpTrack?.('plan_paywall_shown', { source });
  };

  // "Zum Meal Plan hinzufügen". Subscribers pick the day and meal right here (assets/vhp-plan-picker.js).
  // Everyone else: the recipe is saved as a like (the planner reads the same likes document, so it starts there)
  // and a soft paywall explains the planner.
  const addToPlan = (btn, href = state.href, forcedSource) => {
    const d = btn.dataset;
    const source = forcedSource || (btn.closest('.cook') ? 'cook' : d.rsSource || 'sheet');
    const subscriber = Boolean(window.vhpAccount?.subscriber);
    const plannable = d.plannable === 'true';
    document.dispatchEvent(
      new CustomEvent('vhp:plan-add', {
        bubbles: true,
        detail: { id: d.productId, handle: d.handle, title: d.title, url: href, plannable, subscriber },
      })
    );
    if (subscriber && plannable && d.productId && window.vhpPlanPicker) {
      // The sheet tracks recipe_plan_add itself.
      window.vhpPlanPicker.open(
        {
          id: d.productId,
          handle: d.handle,
          title: d.title,
          image: d.image || '',
          slot: d.slot,
          kcal: d.kcal,
          protein: d.protein,
          carbs: d.carbs,
          fat: d.fat,
          time: d.time,
        },
        { source }
      );
      return;
    }
    window.vhpTrack?.('recipe_plan_add', { source, subscriber, plannable, recipe_url: href });
    // Not started the planner yet: the planner explainer in the soft paywall's place (it saves the like itself and
    // says so; assets/vhp-plan-goal.js).
    if (!subscriber && window.vhpPlanExplainer?.open({ where: source, dish: plannable ? d : null })) return;
    if (plannable && window.vhpPlannerLikes) {
      window.vhpPlannerLikes.addLike({
        id: d.productId,
        handle: d.handle,
        title: d.title,
        image: d.image || '',
        slot: d.slot,
        kcal: d.kcal,
        protein: d.protein,
        carbs: d.carbs,
        fat: d.fat,
        time: d.time,
      });
    }
    if (!subscriber) {
      showPaywall(source, d.title, plannable ? d.productId : '');
      return;
    }
    if (!plannable) {
      toast(tr('rsheet.not_plannable'));
      return;
    }
    toast(tr('rsheet.in_favorites'), { href: plannerUrl(), label: tr('common.to_planner') });
  };

  // Calendar button on the swipe card: the sheet button's path (subscribers: day/meal sheet; everyone else:
  // like + paywall).
  document.addEventListener('vhp:card-plan', (event) => {
    const card = event.detail?.card;
    if (!card) return;
    addToPlan(
      {
        dataset: {
          productId: card.id,
          handle: card.handle,
          title: card.title,
          image: card.image,
          slot: card.slot,
          kcal: card.kcal,
          protein: card.protein,
          carbs: card.carbs,
          fat: card.fat,
          time: card.time,
          plannable: 'true',
        },
        closest: () => null,
      },
      card.href || '',
      'card'
    );
  });

  /* ---------------------------------------------------------------- wiring */

  // Tap on the card info (badge, title, macros) or the "Rezept" pill in the inline feed.
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const hit = target.closest(
      '[data-vhp-inline] [data-vhp-swipe-card] :is(.vhp-swipe-card__badge, .vhp-swipe-card__title, .vhp-swipe-card__macros, .vhp-swipe-card__more, .vhp-swipe-card__info, [data-vhp-swipe-recipe])'
    );
    // The planner's own deck (also [data-vhp-inline]) opens the sheet itself.
    if (hit && !hit.closest('[data-vhp-plan-feed]')) {
      const overlay = hit.closest('[data-vhp-reel-overlay]');
      const href = overlay?.querySelector('[data-vhp-swipe-recipe]')?.getAttribute('href');
      if (href && href !== '#') {
        event.preventDefault();
        open(href);
      }
      return;
    }

    const plan = target.closest('[data-rs-plan]');
    if (plan) {
      addToPlan(plan);
      return;
    }

    if (target.closest('[data-rs-paywall-close]')) {
      const node = target.closest('.rs-paywall');
      node?.classList.remove('is-in');
      window.setTimeout(() => node?.remove(), 250);
    }
  });

  window.vhpRecipeSheet = {
    open,
    close: requestClose,
    isOpen: () => state.open,
    full: () => state.open && state.snap !== 'full' && applySnap('full'),
    setBody,
    body: currentBody,
    prefetch: (href) => fetchSheet(href).catch(() => {}),
  };
})();
